"use client";

import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  X,
  CheckCircle,
  XCircle,
  Clock,
  ShieldCheck,
  AlertTriangle,
  User,
  Info,
  Receipt,
  ChevronDown,
  ChevronUp,
  Utensils,
} from "lucide-react";
import { usePaymentRequestStore } from "@/stores/usePaymentRequestStore";
import { useOrderStore } from "@/stores/useOrderStore";
import { toast } from "sonner";
import { useRouter } from "next/navigation";
import type { Order, CartItem } from "@/types/inde_menu";
import { fetchUserById } from "@/features/auth/hooks/useAuthQueries";

interface SplitPaymentModalProps {
  className?: string;
}

export default function SplitPaymentModal({
  className = "",
}: SplitPaymentModalProps) {
  const {
    activeRequest,
    showModal,
    setShowModal,
    acceptPaymentRequest,
    declinePaymentRequest,
  } = usePaymentRequestStore();
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [status, setStatus] = useState<
    "pending" | "processing" | "success" | "error"
  >("pending");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(true);
  const [isShaking, setIsShaking] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const router = useRouter();
  const { fetchOrderById } = useOrderStore();
  const [orderDetails, setOrderDetails] = useState<Order | null>(null);

  const [initiator, setInitiator] = useState<any>(null);

  const formatTime = useCallback((seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  }, []);

  useEffect(() => {
    if (!activeRequest?.initiatorId) return;

    const loadInitiator = async () => {
      try {
        const user = await fetchUserById(activeRequest.initiatorId);
        setInitiator(user);
      } catch (error) {
        console.error("Error fetching initiator details:", error);
      }
    };

    loadInitiator();
  }, [activeRequest]);

  useEffect(() => {
    if (!activeRequest || !showModal) return;

    const calculateTimeRemaining = () => {
      const now = new Date();
      const expiryTime = new Date(activeRequest.expiresAt);
      const diffMs = expiryTime.getTime() - now.getTime();
      return Math.max(0, Math.floor(diffMs / 1000));
    };

    const initialTime = calculateTimeRemaining();
    setTimeRemaining(initialTime);

    const timer = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);

      if (remaining <= 0) {
        clearInterval(timer);
        setIsShaking(true);
        toast.error("Payment request expired");
        setTimeout(() => {
          setIsShaking(false);
          setShowModal(false);
        }, 1000);
      }
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [activeRequest, showModal, setShowModal]);

  useEffect(() => {
    if (!activeRequest || !showModal) return;

    if (activeRequest.orderDetails) {
      setOrderDetails(activeRequest.orderDetails);
    } else {
      fetchOrderById(activeRequest.orderId)
        .then((order) => {
          if (order) {
            setOrderDetails(order);
          } else {
            toast.error("Could not load order details");
          }
        })
        .catch((err) => {
          console.error("Error fetching order details:", err);
          toast.error("Failed to load order details");
        });
    }
  }, [activeRequest, fetchOrderById, showModal]);

  const handleAccept = async () => {
    if (!activeRequest) return;

    setStatus("processing");

    try {
      await acceptPaymentRequest(activeRequest.id);
      setStatus("success");
      setShowConfetti(true);
      toast.success("Payment processed successfully!");

      setTimeout(() => {
        router.push(
          `/dashboards/customer/orders/${activeRequest.orderId}?paid=1&split=1`
        );
        setShowModal(false);
        setShowConfetti(false);
      }, 2500);
    } catch (error) {
      console.error("Payment error:", error);
      setStatus("error");
      toast.error("Payment failed. Please try again.");
      setTimeout(() => {
        setStatus("pending");
      }, 2000);
    }
  };

  const handleDecline = async () => {
    if (!activeRequest) return;

    try {
      await declinePaymentRequest(activeRequest.id);
      toast.info("Payment request declined");
    } catch (error) {
      console.error("Error declining payment:", error);
      toast.error("Failed to decline payment request");
    }
  };

  const handleClose = () => {
    setShowModal(false);
  };

  if (!activeRequest || !showModal) {
    return null;
  }

  return (
    <AnimatePresence>
      {showModal && (
        <>
          {showConfetti && (
            <div className="fixed inset-0 z-50 pointer-events-none">
              {[...Array(50)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full"
                  initial={{
                    x: Math.random() * window.innerWidth,
                    y: window.innerHeight + 10,
                    opacity: 1,
                    rotate: 0,
                  }}
                  animate={{
                    y: -100,
                    x: Math.random() * 100 - 50 + (i % 2 === 0 ? -100 : 100),
                    opacity: 0,
                    rotate: 360,
                  }}
                  transition={{
                    duration: 1.5 + Math.random() * 2,
                    delay: i * 0.05,
                  }}
                />
              ))}
            </div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-gradient-to-br from-slate-900/85 to-slate-800/95 backdrop-blur-md z-50"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
              x: isShaking ? [0, -10, 10, -10, 10, 0] : 0,
            }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{
              type: "spring",
              duration: 0.5,
              x: { duration: 0.5 },
            }}
            className={`fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-md bg-white rounded-2xl shadow-2xl z-50 overflow-hidden border border-slate-200 max-h-[90vh] overflow-y-auto ${className}`}
          >
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-5 text-white relative overflow-hidden">
              <motion.div
                className="absolute inset-0 opacity-20"
                animate={{
                  background: [
                    "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%)",
                    "radial-gradient(circle at 80% 50%, rgba(255,255,255,0.3) 0%, transparent 50%)",
                    "radial-gradient(circle at 50% 20%, rgba(255,255,255,0.3) 0%, transparent 50%)",
                    "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%)",
                  ],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
              />

              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center">
                  <motion.div
                    className="bg-white/10 p-2 rounded-lg mr-3"
                    whileHover={{ rotate: 10, scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <Utensils size={20} />
                  </motion.div>
                  <h2 className="text-xl font-bold">Split Payment Request</h2>
                </div>
                <motion.button
                  whileHover={{
                    rotate: 90,
                    backgroundColor: "rgba(255,255,255,0.2)",
                  }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleClose}
                  className="p-1.5 rounded-full bg-white/10 transition-colors"
                >
                  <X size={18} />
                </motion.button>
              </div>

              <div className="flex items-center mt-3 text-white/90 text-sm relative z-10">
                <motion.div
                  className="flex items-center bg-white/10 py-1 px-2 rounded-full"
                  animate={
                    timeRemaining < 30
                      ? {
                          scale: [1, 1.05, 1],
                          backgroundColor: [
                            "rgba(255,255,255,0.1)",
                            "rgba(239, 68, 68, 0.3)",
                            "rgba(255,255,255,0.1)",
                          ],
                        }
                      : {}
                  }
                  transition={{
                    duration: 1,
                    repeat: timeRemaining < 30 ? Infinity : 0,
                  }}
                >
                  <Clock size={14} className="mr-1.5" />
                  <span className="font-medium">
                    Expires in {formatTime(timeRemaining)}
                  </span>
                </motion.div>
                {timeRemaining < 60 && (
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="ml-2 bg-rose-500 text-xs py-0.5 px-2 rounded-full font-semibold"
                  >
                    Hurry!
                  </motion.div>
                )}
              </div>

              <motion.div
                className="h-1 bg-white/10 rounded-full overflow-hidden mt-3"
                initial={{ width: "100%" }}
                animate={{ width: `${(timeRemaining / 300) * 100}%` }}
                transition={{ duration: 1 }}
              >
                <div className="h-full bg-emerald-400 rounded-full" />
              </motion.div>
            </div>

            <div className="p-5">
              {showInfoBanner && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mb-5 bg-blue-50 p-4 rounded-xl border border-blue-100 relative"
                >
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowInfoBanner(false)}
                    className="absolute top-3 right-3 text-blue-500 hover:text-blue-700"
                  >
                    <X size={16} />
                  </motion.button>
                  <div className="flex items-start">
                    <motion.div
                      className="bg-blue-100 p-2 rounded-lg mr-3"
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatDelay: 5,
                      }}
                    >
                      <Info size={16} className="text-blue-600" />
                    </motion.div>
                    <div>
                      <h3 className="font-medium text-slate-800 mb-1">
                        Payment Request
                      </h3>
                      <p className="text-slate-600 text-sm">
                        {initiator?.name || "Another user"} has requested you to
                        contribute to this order. Please review the order
                        details and amount before accepting or declining this
                        request.
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              <motion.div
                className="mb-5 bg-slate-50 p-4 rounded-xl border border-slate-200"
                whileHover={{ y: -2 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <div className="flex items-center mb-3">
                  <motion.div
                    className="bg-indigo-100 p-1.5 rounded-lg mr-2"
                    whileHover={{ rotate: 5 }}
                  >
                    <User size={14} className="text-indigo-600" />
                  </motion.div>
                  <span className="text-slate-600 text-sm">Requested by:</span>
                  <span className="font-medium text-slate-800 ml-2">
                    {initiator?.name || "Unknown User"}
                  </span>
                </div>

                <div className="flex items-center mb-3">
                  <motion.div
                    className="bg-emerald-100 p-1.5 rounded-lg mr-2"
                    whileHover={{ rotate: 5 }}
                  >
                    <Receipt size={14} className="text-emerald-600" />
                  </motion.div>
                  <span className="text-slate-600 text-sm">Your share:</span>
                  <motion.span
                    className="text-lg font-bold text-emerald-600 ml-2"
                    initial={{ scale: 1 }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 5,
                    }}
                  >
                    {activeRequest.amount.toLocaleString("en-MW", {
                      style: "currency",
                      currency: "MWK",
                    })}
                  </motion.span>
                </div>

                <div className="flex items-center">
                  <motion.div
                    className="bg-cyan-100 p-1.5 rounded-lg mr-2"
                    whileHover={{ rotate: 5 }}
                  >
                    <Receipt size={14} className="text-cyan-600" />
                  </motion.div>
                  <span className="text-slate-600 text-sm">Split Code:</span>
                  <span className="font-medium text-slate-800 bg-slate-100 px-2 py-1 rounded-md ml-2 text-xs">
                    {activeRequest.splitCode}
                  </span>
                </div>
              </motion.div>

              <div className="mb-5">
                <motion.div
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex justify-between items-center cursor-pointer p-3 bg-slate-50 hover:bg-slate-100 rounded-xl transition-all border border-slate-200"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  <h3 className="font-medium text-slate-800 flex items-center">
                    <motion.div
                      className="bg-blue-100 p-1.5 rounded-lg mr-2"
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Receipt size={14} className="text-blue-600" />
                    </motion.div>
                    Order Details
                  </h3>
                  <div className="flex items-center">
                    <span className="text-blue-600 text-sm mr-2">
                      {isExpanded ? "Hide" : "Show"}
                    </span>
                    <motion.div
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {isExpanded ? (
                        <ChevronUp size={16} className="text-blue-600" />
                      ) : (
                        <ChevronDown size={16} className="text-blue-600" />
                      )}
                    </motion.div>
                  </div>
                </motion.div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-3 border border-slate-200 rounded-xl p-4 bg-white space-y-3"
                    >
                      {orderDetails ? (
                        <>
                          <h4 className="font-medium text-slate-800 border-b pb-2">
                            Items in this order:
                          </h4>

                          {orderDetails.items.map((item: CartItem, index) => (
                            <motion.div
                              key={item.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                              className="flex justify-between text-sm py-2 border-b border-slate-100 last:border-b-0"
                            >
                              <div className="flex-1">
                                <span className="text-slate-700 font-medium">
                                  {item.quantity}x {item.name}
                                </span>{" "}
                              </div>
                              <span className="font-medium text-slate-800 whitespace-nowrap">
                                {(item.price * item.quantity).toLocaleString(
                                  "en-MW",
                                  {
                                    style: "currency",
                                    currency: "MWK",
                                  }
                                )}
                              </span>
                            </motion.div>
                          ))}

                          <div className="pt-3 border-t border-slate-200 space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Subtotal</span>
                              <span className="text-slate-800">
                                {orderDetails.subtotal.toLocaleString("en-MW", {
                                  style: "currency",
                                  currency: "MWK",
                                })}
                              </span>
                            </div>

                            {orderDetails.tax > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Tax</span>
                                <span className="text-slate-800">
                                  {orderDetails.tax.toLocaleString("en-MW", {
                                    style: "currency",
                                    currency: "MWK",
                                  })}
                                </span>
                              </div>
                            )}

                            {(orderDetails.serviceCharge ?? 0) > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">
                                  Service Charge
                                </span>
                                <span className="text-slate-800">
                                  {(orderDetails.serviceCharge ?? 0).toLocaleString(
                                    "en-MW",
                                    {
                                      style: "currency",
                                      currency: "MWK",
                                    }
                                  )}
                                </span>
                              </div>
                            )}

                            <div className="flex justify-between font-medium text-base mt-3 pt-3 border-t border-slate-200">
                              <span className="text-slate-800">
                                Total Order Value
                              </span>
                              <span className="text-slate-800">
                                {(
                                  orderDetails.subtotal +
                                  (orderDetails.tax || 0) +
                                  (orderDetails.serviceCharge || 0)
                                ).toLocaleString("en-MW", {
                                  style: "currency",
                                  currency: "MWK",
                                })}
                              </span>
                            </div>

                            <motion.div
                              className="flex justify-between font-medium text-emerald-600 mt-2 text-base py-2 bg-emerald-50 rounded-lg px-2"
                              initial={{ scale: 0.95 }}
                              animate={{ scale: 1 }}
                              transition={{ delay: 0.5 }}
                            >
                              <span>Your Share</span>
                              <span>
                                {activeRequest.amount.toLocaleString("en-MW", {
                                  style: "currency",
                                  currency: "MWK",
                                })}
                              </span>
                            </motion.div>
                          </div>
                        </>
                      ) : (
                        <div className="space-y-3">
                          {[...Array(3)].map((_, i) => (
                            <div key={i} className="flex justify-between">
                              <div className="h-4 bg-slate-200 rounded w-2/3 animate-pulse"></div>
                              <div className="h-4 bg-slate-200 rounded w-1/4 animate-pulse"></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <motion.div
                className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 mb-5"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-start">
                  <motion.div
                    className="bg-emerald-100 p-2 rounded-lg mr-3"
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <ShieldCheck size={16} className="text-emerald-600" />
                  </motion.div>
                  <div>
                    <h3 className="font-medium text-slate-800 text-sm mb-1">
                      Secure Payment
                    </h3>
                    <p className="text-slate-600 text-xs">
                      Your payment is protected by our secure payment system.
                      Your financial information is encrypted and never shared.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="flex flex-col sm:flex-row gap-3 justify-center items-stretch"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <motion.button
                  whileHover={{
                    scale: 1.02,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleDecline}
                  disabled={status === "processing" || status === "success"}
                  className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed max-w-xs mx-auto sm:mx-0"
                >
                  <XCircle size={18} className="mr-2" />
                  Decline
                </motion.button>

                <motion.button
                  whileHover={{
                    scale: 1.02,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAccept}
                  disabled={status !== "pending"}
                  className={`flex-1 py-3.5 rounded-xl font-medium transition-all flex items-center justify-center max-w-xs mx-auto sm:mx-0 ${
                    status === "processing"
                      ? "bg-blue-600 text-white cursor-wait"
                      : status === "success"
                      ? "bg-emerald-600 text-white cursor-default"
                      : status === "error"
                      ? "bg-rose-600 text-white cursor-default"
                      : "bg-emerald-600 hover:bg-emerald-700 text-white"
                  }`}
                >
                  {status === "pending" && (
                    <>
                      <CheckCircle size={18} className="mr-2" />
                      Accept Payment
                    </>
                  )}

                  {status === "processing" && (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        className="mr-2"
                      >
                        <Clock size={18} />
                      </motion.div>
                      Processing...
                    </>
                  )}

                  {status === "success" && (
                    <>
                      <CheckCircle size={18} className="mr-2" />
                      Payment Successful
                    </>
                  )}

                  {status === "error" && (
                    <>
                      <AlertTriangle size={18} className="mr-2" />
                      Payment Failed
                    </>
                  )}
                </motion.button>
              </motion.div>
            </div>

            <AnimatePresence>
              {status === "success" && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="bg-emerald-50 p-4 border-t border-emerald-200"
                >
                  <div className="flex items-center text-emerald-700">
                    <CheckCircle size={16} className="mr-2" />
                    <span className="font-medium">
                      Payment successful! Redirecting to order details...
                    </span>
                  </div>
                </motion.div>
              )}

              {status === "error" && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="bg-rose-50 p-4 border-t border-rose-200"
                >
                  <div className="flex items-center text-rose-700">
                    <AlertTriangle size={16} className="mr-2" />
                    <span className="font-medium">
                      Payment failed. Please try again.
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
